import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/api/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, UserPlus, Trash2, Mail,
  Shield, User, CheckCircle2, AlertCircle, Pencil, KeyRound, Eye, EyeOff,
  Package, FileText, ShieldAlert, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import UserPermissionEditor from '@/components/admin/UserPermissionEditor';
import { ShieldCheck } from 'lucide-react';

export default function AdminAgents() {
  const { user: currentUser, verifyCurrentPassword } = useAuth();

  // --- State ---
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const queryClient = useQueryClient();

  // --- Admin elevation confirmation state ---
  // Eğer pendingElevation set ise: "Bu kullanıcı admin yapılacak — kendi parolanı doğrula"
  // payload: { type: 'invite' | 'update', form?, agentId?, updates? }
  const [pendingElevation, setPendingElevation] = useState(null);
  const [elevationPassword, setElevationPassword] = useState('');
  const [elevationError, setElevationError] = useState('');
  const [elevationVerifying, setElevationVerifying] = useState(false);

  // --- Permission editor sheet ---
  const [permissionEditorUser, setPermissionEditorUser] = useState(null);

  // --- Form State (Invite) ---
  const [inviteForm, setInviteForm] = useState({
    email: '', vorname: '', nachname: '', passwort: '', rolle: 'Agent'
  });
  const [showPassword, setShowPassword] = useState(false);

  // --- Form State (Edit) — rolle dahil ---
  const [editForm, setEditForm] = useState({
    vorname: '', nachname: '', passwort: '', gebiet: '', telefon: '', rolle: 'Agent'
  });
  const [showEditPassword, setShowEditPassword] = useState(false);

  // --- Queries ---
  const { data: users = [] } = useQuery({
    queryKey: ['all-users'],
    queryFn: () => base44.entities.User.list(),
  });

  // --- Mutations ---
  const inviteMutation = useMutation({
    mutationFn: async (form) => {
      return base44.users.inviteUser(form.email, form.rolle, {
        vorname: form.vorname,
        nachname: form.nachname,
        passwort: form.passwort,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['all-users']);
      setInviteForm({ email: '', vorname: '', nachname: '', passwort: '', rolle: 'Agent' });
      setShowInviteSheet(false);
      toast.success('Agent erfolgreich erstellt');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ agentId, updates }) => {
      return base44.users.updateAgent(agentId, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['all-users']);
      setShowEditSheet(false);
      setEditingAgent(null);
      toast.success('Benutzer aktualisiert');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => base44.entities.User.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['all-users']);
      setDeleteConfirm(null);
      toast.success('Benutzer entfernt');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // --- Handlers ---
  const isAdminRole = (role) => (role || '').toLowerCase() === 'admin';

  const handleInvite = (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.passwort) {
      toast.error('E-Mail und Passwort sind erforderlich');
      return;
    }
    // Admin oluşturuluyorsa elevation onayı iste
    if (isAdminRole(inviteForm.rolle)) {
      setPendingElevation({ type: 'invite', form: { ...inviteForm } });
      setElevationPassword('');
      setElevationError('');
      return;
    }
    inviteMutation.mutate(inviteForm);
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setEditForm({
      vorname: agent.vorname || '',
      nachname: agent.nachname || '',
      passwort: '',
      gebiet: agent.gebiet || '',
      telefon: agent.telefon || '',
      rolle: agent.rolle || agent.role || 'Agent',
    });
    setShowEditPassword(false);
    setShowEditSheet(true);
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    const updates = {
      vorname: editForm.vorname,
      nachname: editForm.nachname,
      gebiet: editForm.gebiet,
      telefon: editForm.telefon,
      rolle: editForm.rolle,
    };
    if (editForm.passwort) {
      updates.passwort = editForm.passwort;
    }

    // Rol değişikliği: kullanıcı admin değilken admin yapılıyorsa elevation iste
    const wasAdmin = isAdminRole(editingAgent.rolle || editingAgent.role);
    const willBeAdmin = isAdminRole(editForm.rolle);
    if (!wasAdmin && willBeAdmin) {
      setPendingElevation({ type: 'update', agentId: editingAgent.id, updates });
      setElevationPassword('');
      setElevationError('');
      return;
    }

    // Admin'lik kaldırılıyorsa: son admin olamaz
    if (wasAdmin && !willBeAdmin && admins.length <= 1) {
      toast.error('Der letzte Administrator kann nicht herabgestuft werden.');
      return;
    }

    updateMutation.mutate({ agentId: editingAgent.id, updates });
  };

  // Elevation onayı geldiğinde tetiklenen handler
  const handleElevationConfirm = async () => {
    if (!pendingElevation) return;
    setElevationVerifying(true);
    setElevationError('');
    const result = await verifyCurrentPassword(elevationPassword);
    setElevationVerifying(false);
    if (!result.success) {
      setElevationError(result.error || 'Passwort falsch.');
      return;
    }
    // Onaylandı — orijinal mutation'ı çalıştır
    if (pendingElevation.type === 'invite') {
      inviteMutation.mutate(pendingElevation.form);
    } else if (pendingElevation.type === 'update') {
      updateMutation.mutate({
        agentId: pendingElevation.agentId,
        updates: pendingElevation.updates,
      });
    }
    setPendingElevation(null);
    setElevationPassword('');
  };

  const handleElevationCancel = () => {
    setPendingElevation(null);
    setElevationPassword('');
    setElevationError('');
  };

  // Delete safeguard: self-delete + last-admin engelleri
  const handleDeleteRequest = (targetUser) => {
    if (!targetUser) return;
    if (currentUser && targetUser.id === currentUser.id) {
      toast.error('Sie können Ihren eigenen Account nicht löschen.');
      return;
    }
    if (isAdminRole(targetUser.rolle || targetUser.role) && admins.length <= 1) {
      toast.error('Der letzte Administrator kann nicht entfernt werden.');
      return;
    }
    setDeleteConfirm(targetUser);
  };

  const getRole = (u) => (u.rolle || u.role || '').toLowerCase();
  const getDisplayName = (u) => u.name || [u.vorname, u.nachname].filter(Boolean).join(' ') || u.email;

  const agents = users.filter(u => {
    const r = getRole(u);
    return r === 'agent' || r === 'user';
  });
  const admins = users.filter(u => {
    const r = getRole(u);
    return r === 'admin';
  });
  const warehouseUsers = users.filter(u => {
    const r = getRole(u);
    return r === 'warehouse';
  });
  const shippingUsers = users.filter(u => {
    const r = getRole(u);
    return r === 'shipping';
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PageHeader
                title="Agenten-Verwaltung"
                subtitle={<>{agents.length} Agenten</>}
              />
            </div>
            <Button onClick={() => setShowInviteSheet(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Neuer Agent</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Warehouse Users */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-600">
                <Package className="w-5 h-5" />
                Lager (Depo) ({warehouseUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {warehouseUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Keine Lager-Mitarbeiter</p>
                </div>
              ) : (
                warehouseUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg bg-emerald-50/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{getDisplayName(u)}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(u)}
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRequest(u)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Shipping Users */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-600">
                <FileText className="w-5 h-5" />
                Versand (Sevkiyat) ({shippingUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {shippingUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Keine Versand-Mitarbeiter</p>
                </div>
              ) : (
                shippingUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-4 border rounded-lg bg-amber-50/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{getDisplayName(u)}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(u)}
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRequest(u)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Agents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Agenten ({agents.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {agents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Keine Agenten vorhanden</p>
                </div>
              ) : (
                agents.map(agent => (
                  <div
                    key={agent.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{getDisplayName(agent)}</p>
                        <p className="text-sm text-slate-500">{agent.email}</p>
                        {agent.gebiet && (
                          <p className="text-xs text-slate-400">Gebiet: {agent.gebiet}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(agent)}
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRequest(agent)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Admins */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-500" />
                Administratoren ({admins.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {admins.map(admin => {
                const isSelf = currentUser && admin.id === currentUser.id;
                return (
                  <div
                    key={admin.id}
                    className="flex items-center justify-between p-4 border rounded-lg bg-purple-50/50"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">
                          {getDisplayName(admin)}
                          {isSelf && (
                            <span className="ml-2 text-[10px] uppercase font-bold text-purple-600">
                              (Sie)
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 truncate">{admin.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(admin)}
                        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        title="Bearbeiten"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRequest(admin)}
                        disabled={isSelf}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={isSelf ? 'Eigener Account kann nicht gelöscht werden' : 'Entfernen'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {admins.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Keine Administratoren</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* ========== CREATE AGENT SHEET ========== */}
      <Sheet open={showInviteSheet} onOpenChange={setShowInviteSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Neuen Agent erstellen</SheetTitle>
          </SheetHeader>
          <form onSubmit={handleInvite} className="space-y-5 mt-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="vorname">Vorname</Label>
                <Input
                  id="vorname"
                  value={inviteForm.vorname}
                  onChange={(e) => setInviteForm(f => ({ ...f, vorname: e.target.value }))}
                  placeholder="Max"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nachname">Nachname</Label>
                <Input
                  id="nachname"
                  value={inviteForm.nachname}
                  onChange={(e) => setInviteForm(f => ({ ...f, nachname: e.target.value }))}
                  placeholder="Mustermann"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail Adresse *</Label>
              <Input
                id="email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))}
                placeholder="agent@firma.de"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="passwort">Passwort *</Label>
              <div className="relative">
                <Input
                  id="passwort"
                  type={showPassword ? 'text' : 'password'}
                  value={inviteForm.passwort}
                  onChange={(e) => setInviteForm(f => ({ ...f, passwort: e.target.value }))}
                  placeholder="Sicheres Passwort"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rolle">Rolle</Label>
              <select
                id="rolle"
                value={inviteForm.rolle}
                onChange={(e) => setInviteForm(f => ({ ...f, rolle: e.target.value }))}
                className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="Agent">Agent (Standard)</option>
                <option value="Admin">Administrator</option>
                <option value="Warehouse">Lager</option>
                <option value="Shipping">Versand</option>
                <option value="Buchhaltung">Buchhaltung</option>
              </select>
              {inviteForm.rolle === 'Admin' && (
                <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Administrator-Rechte</strong> — vollständiger Zugriff auf alle Bereiche. Bestätigung mit Ihrem Passwort erforderlich.
                  </span>
                </div>
              )}
            </div>

            {inviteMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                <p className="text-sm text-red-600">
                  {inviteMutation.error?.message || 'Fehler beim Erstellen'}
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInviteSheet(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={inviteMutation.isPending}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {inviteMutation.isPending ? 'Erstelle...' : 'Agent erstellen'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* ========== EDIT AGENT SHEET ========== */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Agent bearbeiten</SheetTitle>
          </SheetHeader>
          {editingAgent && (
            <form onSubmit={handleSaveEdit} className="space-y-5 mt-6">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">E-Mail</p>
                <p className="font-medium">{editingAgent.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-vorname">Vorname</Label>
                  <Input
                    id="edit-vorname"
                    value={editForm.vorname}
                    onChange={(e) => setEditForm(f => ({ ...f, vorname: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-nachname">Nachname</Label>
                  <Input
                    id="edit-nachname"
                    value={editForm.nachname}
                    onChange={(e) => setEditForm(f => ({ ...f, nachname: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-gebiet">Gebiet</Label>
                <Input
                  id="edit-gebiet"
                  value={editForm.gebiet}
                  onChange={(e) => setEditForm(f => ({ ...f, gebiet: e.target.value }))}
                  placeholder="z.B. Berlin, NRW"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-telefon">Telefon</Label>
                <Input
                  id="edit-telefon"
                  type="tel"
                  value={editForm.telefon}
                  onChange={(e) => setEditForm(f => ({ ...f, telefon: e.target.value }))}
                  placeholder="+49 ..."
                />
              </div>

              {/* Rolle Change */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-rolle">Rolle</Label>
                <select
                  id="edit-rolle"
                  value={editForm.rolle}
                  onChange={(e) => setEditForm(f => ({ ...f, rolle: e.target.value }))}
                  className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Agent">Agent (Standard)</option>
                  <option value="Admin">Administrator</option>
                  <option value="Warehouse">Lager</option>
                  <option value="Shipping">Versand</option>
                  <option value="Buchhaltung">Buchhaltung</option>
                </select>
                {editForm.rolle === 'Admin' && !isAdminRole(editingAgent.rolle || editingAgent.role) && (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Administrator-Rechte</strong> — Nach dem Speichern wird Ihre Bestätigung mit Ihrem eigenen Passwort verlangt.
                    </span>
                  </div>
                )}
              </div>

              {/* Password Change */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-sm">Passwort ändern</span>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-passwort">Neues Passwort</Label>
                  <div className="relative">
                    <Input
                      id="edit-passwort"
                      type={showEditPassword ? 'text' : 'password'}
                      value={editForm.passwort}
                      onChange={(e) => setEditForm(f => ({ ...f, passwort: e.target.value }))}
                      placeholder="Leer lassen = nicht ändern"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Leer lassen, um das aktuelle Passwort beizubehalten</p>
                </div>
              </div>

              {updateMutation.isError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-600">{updateMutation.error?.message}</p>
                </div>
              )}

              {/* Permission editor link */}
              <div className="border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditSheet(false);
                    // Kısa delay ile editor açılır — sheet animasyonu çakışmasın
                    setTimeout(() => setPermissionEditorUser(editingAgent), 200);
                  }}
                  className="w-full justify-start min-h-[44px]"
                >
                  <ShieldCheck className="w-4 h-4 mr-2 text-purple-600" />
                  Bildschirm-Berechtigungen anpassen
                </Button>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditSheet(false)}
                  className="flex-1 min-h-[44px]"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 min-h-[44px]"
                >
                  {updateMutation.isPending ? 'Speichere...' : 'Speichern'}
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* Permission Editor Sheet */}
      <UserPermissionEditor
        user={permissionEditorUser}
        open={!!permissionEditorUser}
        onOpenChange={(open) => !open && setPermissionEditorUser(null)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie <strong>{getDisplayName(deleteConfirm || {})}</strong> wirklich entfernen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUserMutation.mutate(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Elevation Confirmation — verlangt das eigene Passwort */}
      <AlertDialog open={!!pendingElevation} onOpenChange={(open) => !open && handleElevationCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Administrator-Rechte erteilen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Person erhält <strong>vollständigen Zugriff</strong> auf alle Bereiche
              des Systems, einschließlich Benutzerverwaltung. Bestätigen Sie mit Ihrem
              eigenen Passwort.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="elevation-password">Ihr Passwort</Label>
            <Input
              id="elevation-password"
              type="password"
              value={elevationPassword}
              onChange={(e) => {
                setElevationPassword(e.target.value);
                if (elevationError) setElevationError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && elevationPassword && !elevationVerifying) {
                  e.preventDefault();
                  handleElevationConfirm();
                }
              }}
              placeholder="••••••••"
              autoFocus
              disabled={elevationVerifying}
            />
            {elevationError && (
              <div className="flex items-start gap-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{elevationError}</span>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleElevationCancel} disabled={elevationVerifying}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleElevationConfirm();
              }}
              disabled={!elevationPassword || elevationVerifying}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {elevationVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Prüfe...
                </>
              ) : (
                <>Bestätigen & Erteilen</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
